import React, { useEffect, useState } from "react";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { useParams } from "react-router-dom";
import {
  frequencyAndDurationEnum,
  levelOfSeverityEnum,
} from "./data";
import QuestionCard from "./question-card";
import { useDispatch, useSelector } from "react-redux";
import { updateClinicalService } from "../../../../../redux/modules/clinical-services/actions";
import SubNavigation from "../../common/sub-navigation";
import { getValidStep } from "../../../text/data";

const Classification = () => {
  const { clinicalServiceObj, clinicalServiceSteps } = useSelector((state) => state.clinicalService);
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [frequencyOfSymptoms, setFrequencyOfSymptoms] = useState(null);
  const [levelOfSeverity, setLevelOfSeverity] = useState(null);
  const [completeSubStep, setCompleteSubStep] = useState(false);

  const frequencyOfSymptomsSelection = (value) => {
    setFrequencyOfSymptoms(value);
  };

  const levelOfSeveritySelection = (value) => {
    setLevelOfSeverity(value);
  };

  useEffect(() => {
    if (clinicalServiceObj.initialAssessment.initialAssessmentForm.treatment) {
      let treatment =
        clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment;
      setFrequencyOfSymptoms(treatment?.frequencyOfSymptoms);
      setLevelOfSeverity(treatment?.levelOfSeverity);
    }
  }, [clinicalServiceObj.initialAssessment.initialAssessmentForm]);

  useEffect(() => {
    if (frequencyOfSymptoms && levelOfSeverity) setCompleteSubStep(true);
    else setCompleteSubStep(false);
  }, [frequencyOfSymptoms, levelOfSeverity]);

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);
      const prevTreatment =
        clinicalServiceObj?.initialAssessment?.initialAssessmentForm?.treatment || {};
      const classificationChanged =
        JSON.stringify(prevTreatment?.frequencyOfSymptoms) !==
          JSON.stringify(frequencyOfSymptoms) ||
        JSON.stringify(prevTreatment?.levelOfSeverity) !==
          JSON.stringify(levelOfSeverity);

      const treatmentUpdate = {
        ...prevTreatment,
        frequencyOfSymptoms,
        levelOfSeverity,
      };

      // Re-classifying invalidates prior pharmacological selections and downstream Education state.
      if (classificationChanged) {
        treatmentUpdate.firstLineTherapyPrescriptions = [];
        treatmentUpdate.stepUpTherapyPrescriptions = [];
        treatmentUpdate.alternativeFirstLineTherapyPrescriptions = [];
        treatmentUpdate.addOnTherapyOralDecongestantsPrescriptions = [];
        treatmentUpdate.addOnTherapyIntranasalDecongestantsPrescriptions = [];
        treatmentUpdate.addOnTherapyOralAntihistaminesPrescriptions = [];
        treatmentUpdate.pharmacologicalSkipAddOns = false;
        treatmentUpdate.pharmacologicalProfileAnalysis = false;
        treatmentUpdate.pharmacologicalProfileAnalysisComplete = false;
        treatmentUpdate.pharmacologicalConfirmationType = 0;
        treatmentUpdate.pharmacologicalExplaination = "";
      }

      const updatedSteps = {
        treatment: treatmentUpdate,
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        serviceSteps: clinicalServiceSteps
      };
      dispatch(updateClinicalService(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, -1);
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateClinicalService(serviceId, updatedSteps));
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Classification of Allergic Rhinitis
                </h4>
                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              <div
                className="grid grid-flow-col auto-cols-[480px] gap-6 scroll-smooth overflow-auto"
                id="classification"
              >
                <QuestionCard
                  className={``}
                  questionTitle={() => (
                    <h3 className="text-xl font-bold text-teal-600 ">
                      {frequencyAndDurationEnum.header}
                    </h3>
                  )}
                  question={() => (
                    <span className="font-semibold ">
                      {frequencyAndDurationEnum.question}
                    </span>
                  )}
                  subquestion={frequencyAndDurationEnum.subquestion}
                  options={frequencyAndDurationEnum.options}
                  optionClick={(value) => frequencyOfSymptomsSelection(value)}
                  values={frequencyOfSymptoms}
                  isMulti={true}
                />

                <QuestionCard
                  className={``}
                  questionTitle={() => (
                    <h3 className="text-xl font-bold text-teal-600 ">
                      {levelOfSeverityEnum.header}
                    </h3>
                  )}
                  question={() => (
                    <span className="font-semibold ">
                      {levelOfSeverityEnum.question}
                    </span>
                  )}
                  subquestion={levelOfSeverityEnum.subquestion}
                  options={levelOfSeverityEnum.options}
                  optionClick={(value) => levelOfSeveritySelection(value)}
                  values={levelOfSeverity}
                  isMulti={true}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px]  ${
              !completeSubStep && "opacity-40 pointer-events-none"
            }`}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>
    </div>
  );
};

export default Classification;
